import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Legend from '../../components/Legend';
import {IDashboardData, IFdcDashboardResult, SurveySlug} from '../../utils/types';
import {FDC_LABELS} from '../labels';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
    surveyPublicId: string;
}

const buildApiKey = (slug: SurveySlug): string => (slug.denominator ? `${slug.numerator.question}_${slug.denominator.question}` : slug.numerator.question);

const buildSlugs = (surveyId: number, surveyPublicId: number): SurveySlug[] => [
    {
        numerator: {survey_id: surveyPublicId, question: 'domanda19'},
        denominator: {survey_id: surveyId, question: 'question10'},
        operation: 'sum',
        key: 'i16',
        roman: 'XVI',
        title: 'Quota dei contributi per progetti in partenariato a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero complessivo dei contributi deliberati per il sostegno di progetti realizzati in partenariato durante l'anno e il numero complessivo dei contributi deliberati durante l'anno.",
    },
    {
        numerator: {survey_id: surveyPublicId, question: 'domanda20'},
        denominator: {survey_id: surveyPublicId, question: 'domanda21'},
        operation: 'sum',
        key: 'i17',
        roman: 'XVII',
        title: 'Momentum delle iniziative congiunte a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di iniziative congiunte avviate con altre FdC nel corso dell'anno e il numero di iniziative congiunte avviate con altre FdC negli ultimi tre anni.",
    },
];

const Dashboard4 = ({container, uoaId, surveyId, surveyPublicId}: Props) => {
    const slugs = buildSlugs(Number(surveyId), Number(surveyPublicId));

    const {data, isLoading, isError} = useQuery<IFdcDashboardResult>({
        queryKey: ['fdc-dashboard4', container, uoaId, surveyId, surveyPublicId],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    with_groups: 1,
                    question_slugs: slugs.map((s) => ({
                        numerator: s.numerator,
                        denominator: s.denominator,
                        operation: s.operation,
                    })),
                },
            });

            const groupNumber: number | null = res.data['__group_number'] ?? null;
            const indicators: IDashboardData = {};
            for (const slug of slugs) {
                const apiKey = buildApiKey(slug);
                indicators[slug.key] = res.data[apiKey] ?? {uoa: null, average: null, group: null};
            }
            return {indicators, groupNumber};
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Legend labels={FDC_LABELS} groupNumber={data.groupNumber} />

            {slugs.map((slug) => (
                <Indicator
                    key={slug.key}
                    roman={slug.roman}
                    title={slug.title}
                    tooltip={slug.tooltip}
                    data={data.indicators[slug.key]}
                    isCount={true}
                    labels={FDC_LABELS}
                />
            ))}
        </Box>
    );
};

export default Dashboard4;
